import { useEffect, useState, useCallback } from "react";
import { api, isAuthenticated, clearTokens } from "@/api";

/* ── Types matching Lovable's interface ── */

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  botId?: string;
  botName?: string;
  onboardingComplete: boolean;
}

export interface Session {
  user: AuthUser;
}

/**
 * useAuth — Zeus JWT auth hook.
 *
 * Provides the same interface as the original Lovable useAuth
 * (session, user, loading, signOut) but backed by Zeus JWT tokens.
 */
export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!isAuthenticated()) {
      setSession(null);
      setLoading(false);
      return;
    }

    const res = await api.get<AuthUser>("/auth/me");
    if (res.ok && res.data) {
      setSession({ user: res.data });
    } else {
      setSession(null);
      clearTokens();
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const signOut = useCallback(() => {
    clearTokens();
    setSession(null);
    window.location.href = "/auth";
  }, []);

  return {
    session,
    user: session?.user ?? null,
    loading,
    signOut,
    refetchUser: fetchUser,
  };
}
